/**
 * Web counterpart of `open-actor-turso-sync-connection.ts`.
 *
 * Opens the actor database AS a `@tursodatabase/sync-wasm` `Database` (sync mode,
 * CDC capturing) and adapts it to the expo-sqlite surface the actor data layer
 * speaks — exactly like native, so the SAME unification holds on web: every actor
 * write flows through this one Turso connection (Turso's `capture_data_changes`
 * is per-connection), and the manual `pull()` / `push()` actions operate on the
 * connection the app writes through.
 *
 * The wasm build stores its synced file in OPFS, keyed by the `path` string, so
 * there is no on-disk path to resolve the way native does — the OPFS name (e.g.
 * `actor.db`) is used directly and surfaced back as `databasePath` for the
 * `emitManualDatabaseChange` plumbing in the adapter.
 *
 * Mirrors native's open contract: bootstrap empty files, bound connect(), and
 * retry without deleting an existing replica. Only a fresh bootstrap created
 * by the current open may be reset on retry. Check the saved local-server URL
 * before connect() can overwrite it and reuse a cursor against another server.
 *
 * Like native, the open migrates a replica that is behind this build in one unlogged
 * transaction, and runs the same updater after every applied download, inside the same
 * lock slot (see `update-actor-schema.ts`). This code runs inside the leader worker, so
 * no other tab's statement can land between a download and the updater.
 */
import { withDeadline } from "@-/dev.improve.haniverse.lib.deadline";
import { createActorSyncGatewayFetch } from "#actor-sync-fetch";
import { adaptTursoToExpoSqlite } from "#turso-adapter";
import { createActorSyncConnectionLock } from "#actor-sync-connection-lock";
import { PUSH_TIMEOUT_MS, pullWithTimeout } from "#lock-turso-sync-phases";
import { withSqliteBusyRetry } from "#with-sqlite-busy-retry";
import type {
  ActorTursoSyncConnection,
  ActorTursoSyncOptions,
} from "#open-actor-turso-sync-connection-types";
import { loadSyncWasm } from "#load-sync-wasm";
import { wipeTursoDatabaseFiles } from "#wipe-turso-database-files";
import { LEADER_WRITE_TABLE } from "#leader-write-ledger";
import {
  assertTursoReplicaOrigin,
  TURSO_REPLICA_FILE_SUFFIXES,
} from "#assert-turso-replica-origin";
import { retryTursoBootstrap } from "#retry-turso-bootstrap";
import {
  ActorForeignHistoryError,
  createActorJournalHead,
} from "@-/dev.improve.haniverse.lib.database.actor.migration-tags";
import { createActorSyncStepTracker } from "#actor-sync-refusal";
import {
  ActorChangeCaptureLostError,
  type ActorSchemaEngine,
  updateActorSchema,
} from "#update-actor-schema";

/** See `ACTOR_PUSH_OPERATIONS_PER_REQUEST` in the native opener; kept equal. */
const PUSH_OPERATIONS_PER_REQUEST = 500;

// Per-attempt cap. Kept BELOW the caller's outer backstop divided by the attempt count, so the
// retry ladder can actually complete: 12s x 3 = 36s worst case, inside the 45s outer cap in
// `actor-database.web.ts` and inside the E2E flows' 60s assertion gates. The previous 20s x 3 = 60s
// could not fit under a 15s caller cap, which is why the retries never ran (see that file).
const CONNECT_TIMEOUT_MS = 12_000;
const CONNECT_MAX_ATTEMPTS = 3;

/**
 * The `Database` of an attempt that is still running after its caller gave up.
 *
 * `pTimeout` in `actor-database.web.ts` rejects the CALLER but cannot cancel the work behind it. The
 * budgets are now nested so this is rare (36s of attempts under a 45s cap) rather than guaranteed,
 * as it was when a 15s cap sat under a single 20s attempt — but "rare" is not "impossible", and the
 * consequence is severe: the abandoned attempt still owns `actor.db`'s OPFS sync access handle,
 * which is EXCLUSIVE per file. It is also unreachable by every close path in the app, because
 * `closeCachedActorDatabaseConnection()` can only close what reached the cache, and a timed-out open
 * never got there.
 *
 * So the next open — the error boundary's "Try again", or a sign-in reopen — constructs a second
 * `Database` on the same path and the browser throws:
 *
 *     Failed to execute 'createSyncAccessHandle' on 'FileSystemFileHandle': Access Handles cannot
 *     be created if there is another open Access Handle or Writable stream associated with the
 *     same file.
 *
 * That is the recurrent `web-url-routing` staging failure: the app renders "Something went wrong"
 * instead of Levels. It never appears in the CI log (the app does not echo it to console) — only in
 * the flow's screenshot artifact.
 *
 * Tracking the in-flight handle here lets the NEXT open reclaim it first. Module scope is the right
 * lifetime: the leak outlives the call that created it, which is the entire problem.
 */
let inFlightDatabase: { close?: () => unknown } | null = null;

/**
 * How long to wait for a stale `close()` before giving up on it.
 *
 * The abandoned attempt is by definition in a bad state, so its `close()` can HANG rather than
 * merely reject — and this runs at the head of every open. An unbounded await here would convert a
 * recoverable error into a permanently unopenable database, which is strictly worse than the leak
 * it is trying to clean up.
 */
const STALE_CLOSE_TIMEOUT_MS = 5000;

/**
 * Release a handle left behind by a previously abandoned attempt.
 *
 * Best-effort by design — if the stale attempt is wedged, `close()` may reject OR hang, and neither
 * must stop the new open from proceeding. Clearing the info first means a hung close is never
 * retried against the same object.
 */
async function releaseAbandonedDatabase(): Promise<void> {
  const stale = inFlightDatabase;
  if (stale === null) {
    return;
  }
  inFlightDatabase = null;
  try {
    // Bounded: on timeout we proceed and let the open fail with the Access-Handles error, which is
    // at least self-describing — rather than hanging the app forever on a close that never settles.
    await withDeadline(
      { deadline: STALE_CLOSE_TIMEOUT_MS, operation: "Closing a stale Turso sync database" },
      async () => {
        await stale.close?.();
      },
    );
  } catch {
    // The stale attempt may be in any state; the point is only to drop its OPFS handle if it can
    // still be dropped. A failure here leaves us no worse off than not trying.
  }
}

/**
 * Open the replica, starting it over when its migration history is not this build's (a file from
 * another branch, or from before the history was squashed into the baseline). A linear, immutable
 * history never produces that, so nothing is carried over: the files are wiped and the open runs
 * again, bootstrapping from the cloud copy. This runs in the leader worker, which is the only
 * holder of the files' access handles, and the failed open has closed them.
 */
export async function openActorTursoSyncConnection(
  options: ActorTursoSyncOptions,
): Promise<ActorTursoSyncConnection> {
  try {
    return await openActorTursoSyncConnectionOnce(options);
  } catch (error) {
    if (!(error instanceof ActorForeignHistoryError)) {
      throw error;
    }
    await wipeTursoDatabaseFiles(options.databaseName);
    return openActorTursoSyncConnectionOnce(options);
  }
}

async function openActorTursoSyncConnectionOnce(
  options: ActorTursoSyncOptions,
): Promise<ActorTursoSyncConnection> {
  // `@tursodatabase/sync-wasm` can't go through the Metro graph (its bundle uses
  // top-level await, which Metro's sync CJS module factory can't represent). So we
  // load it as a NATIVE browser ES module from a public asset
  // (`public/turso-sync-wasm/main.es.js`, mirrored by web-prebuild). See
  // `load-sync-wasm.web.ts` for the full rationale. If the asset is missing (no
  // prebuild) this throws and the caller surfaces the failure.
  const { Database } = await loadSyncWasm();
  // On web the OPFS name IS the path the wasm engine stores under; the actor
  // data layer opens OPFS-root `actor.db`, so we sync the same name.
  const databasePath = options.databaseName;
  // An established file can contain unsent writes and their recovery receipts.
  // Only a file created by THIS open attempt is disposable bootstrap state.
  const root = await navigator.storage.getDirectory();
  const existingFiles = new Map<string, FileSystemFileHandle>();
  for await (const [name, handle] of root.entries()) {
    if (handle.kind === "file") {
      existingFiles.set(name, handle);
    }
  }
  const hadPersistedDatabase = TURSO_REPLICA_FILE_SUFFIXES.some((suffix) =>
    existingFiles.has(`${databasePath}${suffix}`),
  );
  const metadataFile = existingFiles.get(`${databasePath}-info`);
  const metadata = await metadataFile?.getFile();
  assertTursoReplicaOrigin(metadata === undefined ? null : await metadata.text(), options.url);

  // One connection-scoped lock shared by the adapter's statements/transactions and
  // the `pull()` / `push()` exposed below — same rationale as native: prevent an
  // app write transaction's `begin` … `commit` from interleaving with a sync op on
  // this single writer connection. See `actor-sync-connection-lock.ts`.
  // Created once per open; a retry reuses it (a failed `connect()` never handed
  // back a usable handle, so there are no in-flight ops across a retry).
  // A poisoned lock (a holder overran and may still be running in the engine) reports through
  // `onDamaged`, so the owner — the OPFS leader — can end this connection and open a fresh one.
  const lock = createActorSyncConnectionLock({
    name: options.databaseName,
    onDamaged: options.onDamaged,
  });
  // The head of the replica's migration journal, sent with every sync request. A file that is
  // about to be bootstrapped has none; an existing replica's is read right after it opens, which
  // is before its first sync request. Raised BEFORE a migration commits, never after.
  let journalHead = createActorJournalHead([]);
  const steps = createActorSyncStepTracker();
  const fetch = createActorSyncGatewayFetch({
    gatewayUrl: options.gatewayUrl,
    getJournalHead: () => journalHead,
    onAnswer: steps.onAnswer,
  });

  return retryTursoBootstrap({
    hadPersistedDatabase,
    isFinal: (error) => error instanceof ActorForeignHistoryError,
    maxAttempts: CONNECT_MAX_ATTEMPTS,
    // Release stale OPFS access handles before trying to reset disposable files.
    open: async () => {
      journalHead = createActorJournalHead([]);
      const tursoDb = new Database({
        // Local sync servers need no token; avoid an empty Authorization header
        // triggering a CORS preflight for an unauthenticated connection.
        authToken: options.authToken === "" ? undefined : options.authToken,
        // Matches native. A sign-in deliberately wipes actor.db before this open
        // because the remote account is the source of truth; bootstrap that empty
        // file during connect(), before the database provider runs local
        // migrations. Web previously relied on the file merely HAPPENING to be
        // empty, which is why a pre-migrated file made sign-in skip bootstrap and
        // report "Already up to date" against an empty deck.
        bootstrapIfEmpty: !hadPersistedDatabase,
        fetch,
        // A one-shot background check must not hold foreground queries for a long poll.
        longPollTimeoutMs: 0,
        path: databasePath,
        // A long offline backlog goes up as bounded requests, split between transactions.
        pushOperationsThreshold: PUSH_OPERATIONS_PER_REQUEST,
        tablesIgnore: [LEADER_WRITE_TABLE],
        url: options.url,
      });

      // Publish the handle BEFORE the first await that can be abandoned. From here until this
      // attempt settles, `tursoDb` may be holding `actor.db`'s exclusive OPFS access handle with
      // no other info to it anywhere in the app.
      inFlightDatabase = tursoDb;

      // Bound bootstrap `connect()`: a transport-level hang on `/pull-updates`
      // would otherwise block this open indefinitely — and the actor connection is
      // what the root layout suspends on, so an unbounded hang blanks the app.
      try {
        await withDeadline(
          { deadline: CONNECT_TIMEOUT_MS, operation: "Turso sync connect()" },
          () => tursoDb.connect(),
        );
        const updateSchema = async (migrations?: Readonly<Record<string, string>>) => {
          try {
            const update = await updateActorSchema(
              // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- the wasm Database has the raw `all`/`exec`/`run` calls the updater makes
              tursoDb as unknown as ActorSchemaEngine,
              {
                migrations,
                onBeforeMigrate: (journal) => {
                  journalHead = createActorJournalHead(journal);
                },
              },
            );
            journalHead = createActorJournalHead(update.journal);
            return update;
          } catch (error) {
            if (error instanceof ActorChangeCaptureLostError) {
              // Writes here would no longer be recorded for upload: replace the connection.
              lock.poison(error);
            }
            throw error;
          }
        };
        // Nothing else holds this engine yet, so the open itself is the exclusive section.
        await withDeadline(
          { deadline: CONNECT_TIMEOUT_MS, operation: "Updating the study database schema" },
          () => updateSchema(),
        );

        // The wasm `Database` exposes the same `run/all/get/exec/close` surface the
        // adapter calls; the adapter's type import is native-only but erased at
        // runtime, so this structural match is safe.
        // Innermost `SQLITE_BUSY` containment, same as native — see
        // `with-sqlite-busy-retry.ts`.
        const connection = withSqliteBusyRetry(
          adaptTursoToExpoSqlite(
            // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- wasm Database is structurally compatible with the adapter's Turso surface
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- wasm Database is structurally compatible with the adapter's Turso surface
            tursoDb as never,
            databasePath,
            options.databaseName,
            lock,
            undefined,
            false,
            // No app statement may touch the schema or the sync bookkeeping of a replica.
            true,
          ),
        );
        // A failed pull does not poison the connection: the engine keeps syncing after a download
        // it could not apply (probe `REUSE`), so the failure is reported and the next cycle retries.
        const performPull = () =>
          pullWithTimeout({
            pull: () =>
              lock.runExclusive(
                async () => {
                  if (lock.recoveryHold) {
                    throw new Error("Cloud sync is paused for recovery");
                  }
                  const changed = await steps.run(() => tursoDb.pull());
                  if (changed) {
                    // The download replaced the schema with the cloud copy's.
                    await updateSchema();
                  }
                  return changed;
                },
                { kind: "sync-apply", queryText: "pull" },
              ),
          });
        const lockedTursoDb: ActorTursoSyncConnection["tursoDb"] = {
          close: () => tursoDb.close(),
          hasLocalReplica: hadPersistedDatabase,
          // Bounded for the same reason as the native two-phase wrapper (see
          // `lock-turso-sync-phases`): `pull()` is the one post-connect call with no timeout of its
          // own, so a sync that wedges after opening wedges here and produces a spinner that never
          // clears rather than an error. Worse on web than native, since this holds the connection
          // lock for the whole pull — a hung pull blocks every app read behind it.
          //
          // Here the pull and push hold the slot across their network round-trips, so they take the
          // sync holder kinds: their hold deadline is the long sync one, past which the lock is
          // poisoned rather than parking every app read behind a round-trip that never returns.
          pull: () => performPull(),
          push: () =>
            withDeadline({ deadline: PUSH_TIMEOUT_MS, operation: "Actor sync push" }, () =>
              lock.runExclusive(
                async () => {
                  if (lock.recoveryHold) {
                    throw new Error("Cloud sync is paused for recovery");
                  }
                  return steps.run(() => tursoDb.push());
                },
                { kind: "sync-push", queryText: "push" },
              ),
            ),
          // Behind any running push or pull (same lock), so the count reflects what it uploaded.
          stats: () =>
            withDeadline({ deadline: PUSH_TIMEOUT_MS, operation: "Actor sync stats" }, () =>
              lock.runExclusive(async () => tursoDb.stats(), {
                kind: "sync-push",
                queryText: "stats",
              }),
            ),
        };
        // A connection that SUCCEEDS after its caller already timed out is still orphaned: nothing
        // ever consumes this return value, yet it holds the OPFS handle. So the marker is dropped
        // only once the caller actually takes delivery (`markConnectionDelivered` below) — until
        // then it stays published and the next open can reclaim it.
        //
        // Clearing here instead would be actively harmful in the opposite direction too: on the
        // happy path it must not stay published, or the next open's reclaim would close the
        // connection the app is actively using.
        return {
          connection,
          databasePath,
          markConnectionDelivered: () => {
            if (inFlightDatabase === tursoDb) {
              inFlightDatabase = null;
            }
          },
          tursoDb: lockedTursoDb,
          updateSchema: (migrations) =>
            lock.runExclusive(() => updateSchema(migrations), {
              kind: "statement",
              queryText: "update schema",
            }),
        };
      } catch (error) {
        try {
          await tursoDb.close?.();
        } catch {
          // best-effort: the connection may already be in a bad state.
        }
        // Closed above, so there is nothing left to reclaim.
        if (inFlightDatabase === tursoDb) {
          inFlightDatabase = null;
        }
        throw error;
      }
    },
    prepareAttempt: releaseAbandonedDatabase,
    resetBootstrap: () => wipeTursoDatabaseFiles(databasePath),
  });
}
